import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { app } from "../firebase";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/* ===================== ESTILO CLEAN (claro, estilo print) ===================== */
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
    url("https://firebasestorage.googleapis.com/v0/b/bigode-app-fade.firebasestorage.app/o/padrao-sistema%2Fbackground.webp?alt=media&token=bfaa24da-f0d8-48e1-ad45-bbd0f8b5144c")
      no-repeat center center / cover fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  flex-direction: column;
  gap: 50px;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: 20px;
`;

const Card = styled.div`
  background-color: #fff;
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h1`
  font-size: 1.4rem;
  font-weight: 800;
  text-align: center;
  color: #1d1d1d;
  margin-bottom: 10px;
`;

const SubTitle = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  text-align: center;
  margin-bottom: 14px;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  > span {
    display: block;
    height: 100%;
    background: #3a79ed;
    transition: width 220ms ease;
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionBtn = styled.button`
  appearance: none;
  border: 2px solid #d1d5db;
  background: #fff;
  color: #111;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: 150ms ease;
  font-weight: 600;
  font-size: 1rem;
  &:hover {
    border-color: #3a79ed;
    background: #f3f4f6;
  }
  &.active {
    border-color: #3a79ed;
    background: #eff6ff;
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 14px;
  font-size: 1rem;
  outline: none;
  color: #111;
  background: #fff;
  &::placeholder {
    color: #9ca3af;
  }
  &:focus {
    border-color: #3a79ed;
    box-shadow: 0 0 0 3px rgba(58, 121, 237, 0.25);
  }
`;

const Button = styled.button`
  width: 100%;
  margin-top: 4px;
  padding: 14px;
  border-radius: 10px;
  border: none;
  background: #3a79ed;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: 160ms ease;
  &:hover {
    background: #2e63c6;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Small = styled.p`
  color: #555;
  font-size: 0.85rem;
  text-align: center;
`;

const Error = styled.div`
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
`;

/* ===== Keyframes ===== */
const ctabg = keyframes`
  0%,100% { background-position: 0% 50% }
  50% { background-position: 100% 50% }
`;

const starsMoveA = keyframes`
  from { background-position: 0% 0%, -40% -20%; }
  to { background-position: 320% 240%, 280% 200%; }
`;

const starsMoveB = keyframes`
  from { background-position: -90% -70%, -150% -130%; }
  to { background-position: 230% 170%, 170% 110%; }
`;

const arrow = keyframes`
  0%,100% { transform: translateX(0) }
  50% { transform: translateX(6px) }
`;

/* ===== Components ===== */
export const CtaSticky = styled.div`
  position: sticky;
  bottom: 12px;
  z-index: 50;
  display: flex;
  justify-content: center;
  padding: 30px 0px;
`;

export const CtaBoost = styled.a`
  /* vars (mobile-first) */
  --h: 56px;
  --rad: 999px;
  --padX: 18px;

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 12px;
  height: var(--h);
  padding: 0 var(--padX);
  border-radius: var(--rad);
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.2px;
  line-height: 1;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 12px 28px rgba(66, 135, 206, 0.28);
  transform: translateZ(0);
  background: linear-gradient(120deg, #6fb2ff, #4287ce, #2e66b4, #6fb2ff);
  background-size: 300% 300%;
  animation: ${ctabg} 6s ease-in-out infinite;
  border: 1px solid;

  /* estrelas — grupo A */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
    background-image: radial-gradient(
        2px 2px at 20% 30%,
        rgba(255, 255, 255, 0.95) 99%,
        transparent 100%
      ),
      radial-gradient(
        2px 2px at 60% 70%,
        rgba(255, 255, 255, 0.75) 99%,
        transparent 100%
      );
    background-repeat: no-repeat;
    background-position: 0% 0%, -40% -20%;
    background-size: 22px 22px, 26px 26px;
    animation: ${starsMoveA} 4.6s linear infinite;
    will-change: background-position;
  }

  /* estrelas — grupo B (delay) */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
    background-image: radial-gradient(
        2px 2px at 80% 40%,
        rgba(255, 255, 255, 0.9) 99%,
        transparent 100%
      ),
      radial-gradient(
        2px 2px at 35% 80%,
        rgba(255, 255, 255, 0.7) 99%,
        transparent 100%
      );
    background-repeat: no-repeat;
    background-position: -90% -70%, -150% -130%;
    background-size: 20px 20px, 28px 28px;
    animation: ${starsMoveB} 4.6s linear infinite;
    animation-delay: 0.7s;
    will-change: background-position;
  }

  /* conteúdo acima das estrelas */
  > * {
    position: relative;
    z-index: 2;
  }

  /* conteúdo */
  .ico {
    font-size: 1.1em;
  }

  .label {
    white-space: nowrap;
  }

  .arrow {
    font-weight: 900;
    margin-left: 2px;
    display: inline-block;
    transform: translateX(0);
    animation: ${arrow} 1.6s ease-in-out infinite;
    opacity: 0.95;
  }

  /* badge opcional */
  .badge {
    margin-left: 6px;
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.4);
    color: #fff;
    font-weight: 800;
    font-size: 12.5px;
    backdrop-filter: blur(4px);
  }

  /* hover / focus */
  &:hover,
  &:focus-visible {
    box-shadow: 0 16px 36px rgba(66, 135, 206, 0.34);
    outline: none;
  }

  &:active {
    transform: translateY(1px);
  }

  /* mobile tuning */
  @media (max-width: 860px) {
    --h: 54px;
    --padX: 16px;
    font-size: 16px;

    .badge {
      display: inline-flex;
    }
  }
`;

/* ===================== HELPERS ===================== */
const db = getFirestore(app);

function onlyDigits(s = "") {
  return (s || "").replace(/\D/g, "");
}

function formatWhatsApp(d) {
  const v = d.slice(0, 11);
  if (v.length <= 2) return v;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10)
    return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7, 11)}`;
}

function getUTMs() {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ].forEach((k) => {
    const v = p.get(k);
    if (v) utm[k] = v;
  });
  const gclid = p.get("gclid") || p.get("fbclid");
  if (gclid) utm.click_id = gclid;
  return utm;
}

/* ===================== COMPONENTE ===================== */
export default function LeadFormAnuncio() {
  const [step, setStep] = useState(0);
  const [docId, setDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [whatsRaw, setWhatsRaw] = useState("");
  const phoneRef = useRef(null);

  const totalSteps = 4;
  const progress = useMemo(() => Math.round((step / totalSteps) * 100), [step]);

  useEffect(() => {
    setError("");
  }, [step]);

  async function ensureDoc(initialData) {
    if (docId) return docId;
    const payload = {
      origem: "ads_form",
      createdAt: serverTimestamp(),
      ...getUTMs(),
      ...initialData,
    };
    const ref = await addDoc(collection(db, "leadsForm"), payload);
    setDocId(ref.id);
    return ref.id;
  }

  async function saveField(field, value) {
    const id = await ensureDoc({});
    await updateDoc(doc(db, "leadsForm", id), {
      [field]: value,
      updatedAt: serverTimestamp(),
    });
  }

  async function handleSelect(field, value) {
    try {
      setLoading(true);
      if (!docId) await ensureDoc({ [field]: value });
      else await saveField(field, value);

      if (field === "perfil" && value === "cliente") {
        await saveField("status", "descartado_cliente_final");
        setStep(99);
        setLoading(false);
        return;
      }

      if (field === "temSistema" && value === "sim") {
        setStep(2); // vai para satisfação
        setLoading(false);
        return;
      }
      if (field === "temSistema" && value === "nao") {
        setStep(3); // vai para oferta de 90 dias
        setLoading(false);
        return;
      }

      setStep((s) => s + 1);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setError("Ocorreu um erro ao salvar. Tente novamente.");
      setLoading(false);
    }
  }

  function onPhoneChange(e) {
    const digits = onlyDigits(e.target.value).slice(0, 11);
    setWhatsRaw(digits);
    if (phoneRef.current) requestAnimationFrame(() => phoneRef.current?.focus());
  }

  function onPhonePaste(e) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = onlyDigits(text).slice(0, 11);
    setWhatsRaw(digits);
  }

  async function handleWhatsSubmit(e) {
    e.preventDefault();
    const latest = phoneRef.current ? onlyDigits(phoneRef.current.value) : whatsRaw;
    const digits = latest.slice(0, 11);

    if (digits.length < 10 || digits.length > 11) {
      setError("Digite um WhatsApp válido (com DDD).");
      phoneRef.current?.focus();
      return;
    }

    try {
      setLoading(true);
      const id = await ensureDoc({});
      await updateDoc(doc(db, "leadsForm", id), {
        whatsapp: `+55${digits}`,
        status: "capturado",
        finishedAt: serverTimestamp(),
      });
      setStep(100);
    } catch (e) {
      console.error(e);
      setError("Não foi possível finalizar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }


  /* ===================== TELAS ===================== */
  function ScreenPerfil() {
    return (
      <>
        <SubTitle>Quem é você?</SubTitle>
        <Options>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("perfil", "dono")}
          >
            Dono de barbearia
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("perfil", "barbeiro")}
          >
            Barbeiro autônomo
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("perfil", "cliente")}
          >
            Cliente final
          </OptionBtn>
        </Options>
      </>
    );
  }

  function ScreenTemSistema() {
    return (
      <>
        <SubTitle>Você já usa algum sistema de agendamento?</SubTitle>
        <Options>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("temSistema", "sim")}
          >
            Sim
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("temSistema", "nao")}
          >
            Não
          </OptionBtn>
        </Options>
      </>
    );
  }

  function ScreenSatisfacao() {
    return (
      <>
        <SubTitle>
          Você está satisfeito com o seu sistema?
        </SubTitle>
        <Options>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("satisfacao", "satisfeito")}
          >
            Estou satisfeito
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("satisfacao", "mais_barato")}
          >
            Quero um mais barato e com página exclusiva
          </OptionBtn>
        </Options>
      </>
    );
  }

  function ScreenOferta() {
    return (
      <>
        <SubTitle>
          Gostaria de testar o melhor sistema por 90 dias grátis?
        </SubTitle>
        <Options>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("teste90", "sim")}
          >
            Sim, quero testar
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("teste90", "nao")}
          >
            Não tenho interesse
          </OptionBtn>
        </Options>
      </>
    );
  }

  function ScreenQtd() {
    return (
      <>
        <SubTitle>Quantos barbeiros trabalham com você?</SubTitle>
        <Options>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("qtdBarbeiros", "1")}
          >
            Só eu
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("qtdBarbeiros", "2-5")}
          >
            2 a 5
          </OptionBtn>
          <OptionBtn
            disabled={loading}
            onClick={() => handleSelect("qtdBarbeiros", "6+")}
          >
            6 ou mais
          </OptionBtn>
        </Options>
      </>
    );
  }

  function ScreenWhats() {
    const formatted = formatWhatsApp(whatsRaw);
    return (
      <>
        <SubTitle>Receba seu acesso grátis</SubTitle>
        <form onSubmit={handleWhatsSubmit}>
          <Input
            ref={phoneRef}
            type="tel"
            autoComplete="tel"
            inputMode="numeric"
            placeholder="(11) 91234-5678"
            value={formatWhatsApp(whatsRaw)}
            onChange={onPhoneChange}
            onPaste={onPhonePaste}
            aria-invalid={!!error}
            maxLength={15} // "(99) 99999-9999" tem 15 chars
          />
          <Button disabled={loading} type="submit">
            Continuar
          </Button>
        </form>
      </>
    );
  }

  function ScreenEncerradoCliente() {
    return (
      <>
        <Title>Obrigado pelo interesse!</Title>
        <SubTitle>
          Este formulário é exclusivo para barbearias e barbeiros.
        </SubTitle>
      </>
    );
  }

  function ScreenConcluido() {
    return (
      <>
        <Title>Pronto! 🚀</Title>
        <SubTitle>
          Você recebeu 90 dias de acesso gratuito à plataforma.
        </SubTitle>
        <CtaSticky>
          <CtaBoost
            href="https://dash.bigode.app/cadastro"
            target="_blank"
            rel="noopener"
            id="cta-teste"
            aria-label="Iniciar teste grátis por 3 meses, sem cartão"
          >
            <span className="ico">✂️</span>
            <span className="label">Faça o teste grátis agora</span>
            {/* opcional:
        <span className="badge">3 meses grátis</span>
        <span className="arrow">→</span>
        */}
          </CtaBoost>
        </CtaSticky>
      </>
    );
  }

  /* ===================== RENDER ===================== */
  const content = (() => {
    if (step === 99) return <ScreenEncerradoCliente />;
    if (step === 100) return <ScreenConcluido />;
    if (step === 0) return <ScreenPerfil />;
    if (step === 1) return <ScreenTemSistema />;
    if (step === 2) return <ScreenSatisfacao />;
    if (step === 3) return <ScreenQtd />;
    if (step === 4) return <ScreenOferta />;
    return <ScreenWhats />;
  })();

  const showProgress = step >= 0 && step <= totalSteps;
  const percent = Math.min(100, progress);

  return (
    <Container>
      <img
        src="https://firebasestorage.googleapis.com/v0/b/bigode-app-fade.firebasestorage.app/o/padrao-sistema%2Flogo-branca.png?alt=media&token=fefbad7c-4fd3-4691-91a8-f5ea18b89203"
        alt="logo"
        style={{ width: "200px" }}
      />

      <Card>
        {showProgress && (
          <ProgressBar>
            <span style={{ width: `${percent}%` }} />
          </ProgressBar>
        )}
        {content}
        {error && <Error>{error}</Error>}
        <Small>Bigode.app — Responda rapidinho e libere seu teste grátis</Small>
      </Card>
    </Container>
  );
}
